import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output
} from '@angular/core';

/**
 * Wartownik przewijania: element, który zgłasza każde przejście przez krawędź kadru —
 * wjazd w widok (`reached`) i wyjazd z niego (`passed`).
 *
 * `IntersectionObserver` zamiast nasłuchu scrolla — nie generuje burzy zdarzeń przy
 * każdym przewinięciu i nie musi wiedzieć, który element strony właściwie się przewija.
 *
 * Wartownik ma zostawać w drzewie także wtedy, gdy nie ma już czego dociągać: `*ngIf`
 * unieważniłby element, który obserwator trzyma. Decyzję „czy jest jeszcze co ładować"
 * podejmuje odbiorca zdarzenia.
 *
 * Uwaga na zachowanie obserwatora: zgłasza się na PRZEJŚCIU przez próg, nie w kółko.
 * Porcja, która nie wypchnęła wartownika poza ekran, nie ma więc jak zamówić następnej —
 * od tego jest `loadedCount`. Przy nieudanym pobraniu lista nie rośnie, więc i tam widok
 * musi dawać użytkownikowi jawny sposób na ponowienie.
 *
 * Callback obserwatora leci poza strefą Angulara, więc emisję wracamy do niej przez
 * `ngZone.run` — inaczej zmiana stanu nie odpaliłaby detekcji zmian.
 */
@Directive({
  selector: '[appScrollSentinel]'
})
export class ScrollSentinelDirective implements AfterViewInit, OnDestroy {

  /** Ile pikseli przed wartownikiem uznajemy, że „już widać". */
  @Input() sentinelMargin = '400px';

  /**
   * Kontener, względem którego liczymy widoczność. Domyślnie okno — wtedy wartownik
   * reaguje na przewijanie całej strony.
   *
   * Podanie elementu z własnym scrollem sprawia, że lista dociąga się od SWOJEGO
   * przewinięcia, niezależnie od reszty widoku. Wtedy też `sentinelMargin` odmierza
   * się od krawędzi tego kontenera, a nie okna — bez tego zapas na wcześniejsze
   * zamówienie porcji nie działa, bo przodek z `overflow` i tak przycina wartownika
   * do zera, zanim margines zdąży cokolwiek zmienić.
   */
  @Input() scrollRoot: Element = null;

  /** Ile wierszy stoi już na liście. Znaczenie ma sama ZMIANA tej liczby, nie wartość. */
  @Input() set loadedCount(rows: number) {
    this.recheckVisibility();
  }

  @Output() reached = new EventEmitter<void>();

  /** Przejście w drugą stronę: wartownik zniknął z kadru górą albo dołem. */
  @Output() passed = new EventEmitter<void>();

  private observer: IntersectionObserver;

  constructor(private host: ElementRef, private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.observer = new IntersectionObserver(
      entries => {
        const inFrame = entries[entries.length - 1].isIntersecting;

        this.ngZone.run(() => inFrame ? this.reached.emit() : this.passed.emit());
      },
      { root: this.scrollRoot || null, rootMargin: this.sentinelMargin }
    );

    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /** Świeżo obserwowany element dostaje zgłoszenie zawsze — nawet gdy nic nie przeciął. */
  private recheckVisibility(): void {
    if (!this.observer) {
      return;
    }

    this.observer.unobserve(this.host.nativeElement);
    this.observer.observe(this.host.nativeElement);
  }
}
